import DOMPurify from "dompurify";
import { marked } from "marked";

/**
 * Project keys are 2–20 letters / digits / underscores in any case (YouTrack allows `SYZPOPClone1`), so a bare
 * `word-12` is only a key when `word` is a project we know: `projectKeys` (lower-cased) gates every match. Without
 * the set (no schema yet) only the classic upper-case form links, which cannot hit prose like `utf-8`.
 */
export const ISSUE_KEY_RE = /\b([A-Za-z][A-Za-z0-9_]{1,19})-(\d+)\b/g;
/** Knowledge Base article key `KEY-A-N` (M11). Cannot collide with an issue key: `A-N` alone is too short for one. */
export const ARTICLE_KEY_RE = /\b([A-Za-z][A-Za-z0-9_]{1,19})-A-(\d+)\b/g;
const UPPER_KEY = /^[A-Z][A-Z0-9]{1,9}$/;
let projectKeys: ReadonlySet<string> | null = null;
const isProject = (key: string): boolean => (projectKeys ? projectKeys.has(key.toLowerCase()) : UPPER_KEY.test(key));

/** Every `KEY-N` mentioned in a Markdown text (unique; keys of known projects only when `keys` is given). */
export function extractKeys(md: string, keys?: ReadonlySet<string>): string[] {
  const out = new Set<string>();
  for (const m of md.matchAll(ISSUE_KEY_RE)) if (keys ? keys.has(m[1]!.toLowerCase()) : UPPER_KEY.test(m[1]!)) out.add(m[0]);
  return [...out];
}

const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogv|ogg)(\?[^#]*)?(#.*)?$/i;
/** Attachment urls known to be videos for the text being rendered (set by renderMarkdown; urls without an extension). */
let videoUrls: ReadonlySet<string> = new Set();
const isVideo = (src: string): boolean => VIDEO_EXT.test(src) || videoUrls.has(src);
const esc = (s: string): string => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
/** YouTrack's `{width=70%}` / `{height=300}` after an image: bare numbers are pixels. */
const sizeStyle = (attrs: string): string => {
  const out: string[] = [];
  for (const m of attrs.matchAll(/\b(width|height)=(\d+(?:\.\d+)?%?)(?:px)?(?=[\s},;]|$)/g)) out.push(`${m[1]}:${m[2]}${/%$/.test(m[2]!) ? "" : "px"}`);
  return out.length ? ` style="${out.join(";")}"` : "";
};

marked.use({
  gfm: true,
  breaks: true,
  // Turn bare issue keys into links; the view marks resolved ones after lookup.
  extensions: [
    {
      // `![alt](src "title"){width=70%}` — YouTrack renders a video file in image syntax as a player, and
      // takes a size attribute block after it. Runs before marked's own image tokenizer.
      name: "media",
      level: "inline",
      start(src) {
        return src.indexOf("![");
      },
      tokenizer(src) {
        // Destination may contain spaces: YouTrack writes attachment names as-is (`![](Screen Recording 12.00.12.mp4)`).
        const m = /^!\[([^\]]*)\]\(\s*(<[^>]*>|[^)]*?)(?:\s+("[^"]*"|'[^']*'))?\s*\)(?:\{([^}]*)\})?/.exec(src);
        if (!m) return undefined;
        return { type: "media", raw: m[0], alt: m[1], href: m[2]!.replace(/^<|>$/g, ""), title: m[3]?.slice(1, -1) ?? null, attrs: m[4] ?? "" };
      },
      renderer(token) {
        const t = token as unknown as { alt: string; href: string; title: string | null; attrs: string };
        const title = t.title ? ` title="${esc(t.title)}"` : "";
        if (isVideo(t.href)) return `<video controls preload="metadata" src="${esc(t.href)}"${title}${sizeStyle(t.attrs)}></video>`;
        return `<img src="${esc(t.href)}" alt="${esc(t.alt)}"${title}${sizeStyle(t.attrs)}>`;
      },
    },
    {
      // `NGB-A-24` → article link; listed before issueKey (which cannot match it anyway).
      name: "articleKey",
      level: "inline",
      start(src) {
        return src.search(/\b[A-Za-z][A-Za-z0-9_]{1,19}-A-\d+\b/);
      },
      tokenizer(src) {
        const m = /^([A-Za-z][A-Za-z0-9_]{1,19})-A-\d+\b/.exec(src);
        if (!m || !isProject(m[1]!)) return undefined;
        return { type: "articleKey", raw: m[0], key: m[0] };
      },
      renderer(token) {
        const key = String((token as unknown as { key: string }).key);
        return `<a class="article-link" href="/article/${key}" data-article-key="${key}">${key}</a>`;
      },
    },
    {
      name: "issueKey",
      level: "inline",
      start(src) {
        return src.search(/\b[A-Za-z][A-Za-z0-9_]{1,19}-\d+\b/);
      },
      tokenizer(src) {
        const m = /^([A-Za-z][A-Za-z0-9_]{1,19})-\d+\b/.exec(src);
        if (!m || !isProject(m[1]!)) return undefined;
        return { type: "issueKey", raw: m[0], key: m[0] };
      },
      renderer(token) {
        const key = String((token as unknown as { key: string }).key);
        return `<a class="issue-link" href="/issue/${key}" data-issue-key="${key}">${key}</a>`;
      },
    },
  ],
});

/**
 * Markdown → sanitized HTML. Links open in the same tab; issue links are handled by the view.
 * `videoUrls`: attachment urls (no extension) that should render as a player.
 */
export function renderMarkdown(md: string, opts: { videoUrls?: ReadonlySet<string>; projectKeys?: ReadonlySet<string> } = {}): string {
  videoUrls = opts.videoUrls ?? new Set();
  projectKeys = opts.projectKeys ?? null;
  try {
    const html = marked.parse(md, { async: false });
    return DOMPurify.sanitize(html, { ADD_ATTR: ["data-issue-key", "data-article-key", "target", "controls", "preload"] });
  } finally {
    videoUrls = new Set();
    projectKeys = null;
  }
}
